import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import StepContainer from '../../components/onboarding/StepContainer';
import DynamicUrlList from '../../components/onboarding/DynamicUrlList';
import { useOnboardingStore } from '../../stores/onboardingStore';
import apiClient from '../../lib/api';

export default function ContentSourcesStep() {
  const { t } = useTranslation();
  const { workspaceData, contentSources, saveContentSources, nextStep, previousStep } =
    useOnboardingStore();

  const [isDiscovering, setIsDiscovering] = useState(false);
  const [discoveryError, setDiscoveryError] = useState<string | null>(null);
  const [discoverySource, setDiscoverySource] = useState<string | null>(null);

  // Auto-discover content sources on mount
  useEffect(() => {
    const discoverContent = async () => {
      // Skip if no website or already has content
      if (
        !workspaceData.website ||
        contentSources.blogUrls.length > 0 ||
        contentSources.productUrls.length > 0
      ) {
        return;
      }

      setIsDiscovering(true);
      setDiscoveryError(null);

      try {
        const response = await apiClient.post('/utils/discover-content', {
          url: workspaceData.website,
          maxUrls: 5,
          maxAgeMonths: 12,
        });

        if (response.data?.success && response.data?.data) {
          const { blogUrls, productUrls, source } = response.data.data;

          // Auto-populate discovered URLs
          saveContentSources({
            blogUrls: blogUrls.map((item: any) => item.url).slice(0, 5),
            productUrls: productUrls.map((item: any) => item.url).slice(0, 5),
          });

          setDiscoverySource(source);
        }
      } catch (error: any) {
        console.error('Failed to discover content:', error);
        setDiscoveryError(
          error.response?.data?.message || 'Could not auto-discover content sources'
        );
      } finally {
        setIsDiscovering(false);
      }
    };

    discoverContent();
  }, []); // Empty deps: only run once on mount

  const handleNext = () => {
    // TODO: In production, save to backend
    // await apiClient.patch(`/workspace/${workspaceId}/content-sources`, contentSources);
    nextStep();
  };

  const handleSkip = () => {
    // Clear content sources and skip
    saveContentSources({ blogUrls: [], productUrls: [] });
    nextStep();
  };

  return (
    <StepContainer
      title={t('onboarding.contentSources.title')}
      description={t('onboarding.contentSources.subtitle')}
      onNext={handleNext}
      onBack={previousStep}
      showSkip={true}
      onSkip={handleSkip}
    >
      <div className="space-y-8">
        {/* Loading Indicator */}
        {isDiscovering && (
          <div className="p-4 bg-blue-50 dark:bg-blue-900/20 border-2 border-blue-500 rounded-lg">
            <div className="flex items-start gap-3">
              <span className="text-2xl animate-spin">⏳</span>
              <div>
                <h3 className="font-semibold mb-1 text-blue-900 dark:text-blue-100">
                  Discovering content sources...
                </h3>
                <p className="text-sm text-blue-800 dark:text-blue-200">
                  Analyzing your website for blog posts and product pages...
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Success Banner */}
        {!isDiscovering && discoverySource && (
          <div className="p-4 bg-green-50 dark:bg-green-900/20 border-2 border-green-500 rounded-lg">
            <div className="flex items-start gap-3">
              <span className="text-2xl">✓</span>
              <div>
                <h3 className="font-semibold mb-1 text-green-900 dark:text-green-100">
                  Content discovered from{' '}
                  {discoverySource === 'llm.txt'
                    ? 'llm.txt'
                    : discoverySource === 'sitemap.xml'
                    ? 'sitemap.xml'
                    : 'homepage'}
                </h3>
                <p className="text-sm text-green-800 dark:text-green-200">
                  Found {contentSources.blogUrls.length} blog posts and{' '}
                  {contentSources.productUrls.length} product pages. You can edit or add more
                  below.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Error Banner */}
        {!isDiscovering && discoveryError && (
          <div className="p-4 bg-orange-50 dark:bg-orange-900/20 border-2 border-orange-500 rounded-lg">
            <div className="flex items-start gap-3">
              <span className="text-2xl">⚠️</span>
              <div>
                <h3 className="font-semibold mb-1 text-orange-900 dark:text-orange-100">
                  Could not auto-discover content
                </h3>
                <p className="text-sm text-orange-800 dark:text-orange-200">
                  {discoveryError}. You can manually add URLs below.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Info Banner - only show if no discovery happened */}
        {!isDiscovering && !discoverySource && !discoveryError && (
          <div className="p-4 bg-blue-50 dark:bg-blue-900/20 border-2 border-blue-500 rounded-lg">
            <div className="flex items-start gap-3">
              <span className="text-2xl">ℹ️</span>
              <div>
                <h3 className="font-semibold mb-1 text-blue-900 dark:text-blue-100">
                  {t('onboarding.contentSources.infoTitle')}
                </h3>
                <p className="text-sm text-blue-800 dark:text-blue-200">
                  {t('onboarding.contentSources.infoDesc')}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Blog Posts */}
        <div>
          <DynamicUrlList
            label={t('onboarding.contentSources.blogPosts')}
            placeholder="https://yourblog.com/post-1"
            urls={contentSources.blogUrls}
            onChange={(urls) =>
              saveContentSources({ ...contentSources, blogUrls: urls })
            }
            maxUrls={5}
          />
          <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
            {t('onboarding.contentSources.blogHelp')}
          </p>
        </div>

        {/* Product/Service Pages */}
        <div>
          <DynamicUrlList
            label={t('onboarding.contentSources.productPages')}
            placeholder="https://yoursite.com/product-1"
            urls={contentSources.productUrls}
            onChange={(urls) =>
              saveContentSources({ ...contentSources, productUrls: urls })
            }
            maxUrls={5}
          />
          <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
            {t('onboarding.contentSources.productHelp')}
          </p>
        </div>
      </div>
    </StepContainer>
  );
}
